import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchCreators, fetchYoutubeVideos, fetchLiveStreams } from "@/lib/api";
import { Users, Play, Video, Sparkles, Radio } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import { sfx } from "@/lib/sounds";
import { motion } from "framer-motion";

function CreatorCard({ c, live = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.4 }}
      className="relative rounded-2xl glass overflow-hidden hover-lift"
      data-testid={`creator-card-${c.id}`}
    >
      <div className="relative h-24 overflow-hidden">
        {c.banner ? <img src={c.banner} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full bg-gradient-to-br from-[#FFBF00]/40 to-[#FF8C00]/20" />}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
        {c.featured_creator && (
          <div className="absolute top-2 right-2 flex items-center gap-1 px-2 py-1 rounded-full bg-[#FFBF00] text-black text-[10px] uppercase tracking-widest font-bold">
            <Sparkles size={10} /> Featured
          </div>
        )}
        {live && (
          <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full bg-red-500 text-white text-[10px] uppercase tracking-widest font-bold animate-pulse">
            <Radio size={10} /> LIVE
          </div>
        )}
      </div>
      <div className="p-4 -mt-8 relative">
        <img src={c.avatar_url || c.avatar} alt="" className="w-14 h-14 rounded-lg border-2 border-black object-cover" />
        <Link to={`/creators/${c.id}`} onClick={() => sfx.click()} className="block mt-2">
          <div className="font-hero text-xl leading-none">{c.name}</div>
          {c.mc_username && <div className="text-[11px] font-mono text-[#FFBF00] mt-1">{c.mc_username}</div>}
        </Link>
        <div className="flex items-center gap-4 mt-3 text-[11px] text-white/60">
          <span className="flex items-center gap-1"><Users size={11} /> {(c.followers_count || 0).toLocaleString("en-IN")}</span>
          {c.creator_level > 0 && <span className="text-[#FFBF00]">Lvl {c.creator_level}</span>}
        </div>
      </div>
    </motion.div>
  );
}

function VideoCard({ v }) {
  return (
    <div className="rounded-2xl glass overflow-hidden hover-lift" data-testid={`video-card-${v.id}`}>
      <div className="relative aspect-video overflow-hidden">
        <img src={v.thumbnail} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 grid place-items-center bg-black/30 opacity-0 hover:opacity-100 transition-opacity">
          <div className="w-14 h-14 rounded-full bg-[#FFBF00] grid place-items-center text-black">
            <Play size={22} fill="currentColor" />
          </div>
        </div>
        {v.is_live && (
          <div className="absolute top-2 left-2 px-2 py-1 rounded-full bg-red-500 text-white text-[10px] uppercase tracking-widest font-bold animate-pulse flex items-center gap-1">
            <Radio size={10} /> LIVE
          </div>
        )}
      </div>
      <div className="p-4">
        <div className="text-sm font-semibold line-clamp-2">{v.title}</div>
        <div className="text-[11px] text-white/50 mt-2 flex items-center gap-2">
          {v.creator_avatar && <img src={v.creator_avatar} alt="" className="w-5 h-5 rounded" />}
          <span className="truncate">{v.creator_name}</span>
        </div>
      </div>
    </div>
  );
}

export default function CreatorHub() {
  const [creators, setCreators] = useState([]);
  const [videos, setVideos] = useState([]);
  const [live, setLive] = useState([]);

  useEffect(() => {
    fetchCreators().then(setCreators).catch(() => {});
    fetchYoutubeVideos(24).then(setVideos).catch(() => {});
    fetchLiveStreams().then(setLive).catch(() => {});
  }, []);

  const featured = creators.filter((c) => c.featured_creator);
  const recent = [...creators].sort((a, b) => (b.creator_since || "").localeCompare(a.creator_since || "")).slice(0, 6);

  return (
    <div className="pt-32 pb-24 px-4 sm:px-6" data-testid="page-creator-hub">
      <div className="max-w-7xl mx-auto">
        <SectionHeader
          eyebrow="Creator Program"
          title="LosTDC MEDIA HUB"
          subtitle="Create || Connect || Grow"
        />
        <div className="flex gap-2 mb-10">
          <Link to="/creators/apply" onClick={() => sfx.pling()} data-testid="creator-apply-cta"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#FFBF00] hover:bg-[#FF8C00] text-black text-xs uppercase tracking-widest font-semibold">
            <Sparkles size={14} /> Apply to be a Creator
          </Link>
        </div>

        {/* LIVE NOW */}
        {live.length > 0 && (
          <section className="mb-14">
            <div className="flex items-center gap-2 mb-4">
              <Radio size={16} className="text-red-500" />
              <div className="text-[11px] uppercase tracking-[0.4em] text-red-400">Live Now</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {live.map((v) => <VideoCard key={v.id} v={v} />)}
            </div>
          </section>
        )}

        {/* FEATURED */}
        {featured.length > 0 && (
          <section className="mb-14">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles size={16} className="text-[#FFBF00]" />
              <div className="text-[11px] uppercase tracking-[0.4em] text-[#FFBF00]">Featured Creators</div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {featured.map((c) => <CreatorCard key={c.id} c={c} />)}
            </div>
          </section>
        )}

        {/* ALL CREATORS */}
        <section className="mb-14">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[11px] uppercase tracking-[0.4em] text-white/60">All Creators</div>
              <div className="text-white/50 text-sm mt-1">{creators.length} approved creator{creators.length === 1 ? "" : "s"}</div>
            </div>
          </div>
          {creators.length === 0 ? (
            <div className="rounded-2xl glass p-16 text-center text-white/50">
              <Sparkles className="mx-auto mb-3 text-[#FFBF00]" size={28} />
              <div className="font-hero text-2xl mb-2">No creators yet</div>
              <div className="text-sm">Be the first — <Link to="/creators/apply" className="text-[#FFBF00] hover:underline">apply here</Link>.</div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {creators.map((c) => <CreatorCard key={c.id} c={c} />)}
            </div>
          )}
        </section>

        {/* VIDEOS */}
        <section className="mb-14">
          <div className="flex items-center gap-2 mb-4">
            <Video size={16} className="text-[#FFBF00]" />
            <div className="text-[11px] uppercase tracking-[0.4em] text-[#FFBF00]">Latest Videos</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {videos.map((v) => <VideoCard key={v.id} v={v} />)}
          </div>
          <p className="mt-4 text-xs text-white/40">
            Videos with <span className="font-mono text-[#FFBF00]">#lostdrkc #lostinlostdc #lostdcnetwork</span>.
          </p>
        </section>
      </div>
    </div>
  );
}
